/**
 * CloudWatch RUM (aws-rum-web) — errors-only v1.
 *
 * Init from app bootstrap (`src/main.jsx`). Session attributes for agency context
 * are updated from the analytics identify/update path once user data is known.
 *
 * @see https://lyt.atlassian.net/browse/PRD-2905
 */
import { AwsRum } from 'aws-rum-web';

import { env } from 'src/config/env';
import { getApplicationRelease } from 'src/config/applicationRelease';

let awsRum = null;

const hasRequiredConfig = () =>
    Boolean(env.cloudwatchRumApplicationId && env.cloudwatchRumIdentityPoolId);

/**
 * Initialize CloudWatch RUM when env vars are present. Safe no-op when missing
 * (local/MR builds without RUM config). Errors during init are swallowed so the
 * app still boots if RUM fails.
 *
 * @returns {AwsRum|null}
 */
export const initializeCloudWatchRum = () => {
    if (awsRum) {
        return awsRum;
    }

    if (!hasRequiredConfig()) {
        return null;
    }

    try {
        const region = env.cloudwatchRumRegion || 'us-west-2';
        const release = getApplicationRelease();
        awsRum = new AwsRum(
            env.cloudwatchRumApplicationId,
            release,
            region,
            {
                sessionSampleRate: 1,
                identityPoolId: env.cloudwatchRumIdentityPoolId,
                endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
                // Auto JS-error telemetry is incidental; v1 product signal is
                // intentional custom events (e.g. ErrorBoundary). Keep errors
                // enabled so the pipe is easy to verify; dashboards should key
                // off custom events, not console noise.
                telemetries: ['errors'],
                allowCookies: false,
                enableXRay: false,
                sessionAttributes: {
                    environment: env.environment || 'unknown',
                    release,
                },
            },
        );
    } catch (error) {
        console.error('CloudWatch RUM initialization failed:', error);
        awsRum = null;
    }

    return awsRum;
};

/**
 * @returns {AwsRum|null}
 */
export const getCloudWatchRum = () => awsRum;

/**
 * Custom event type for ErrorBoundary user-visible fallbacks (PRD-2906).
 * Must match RUM custom-event type pattern: [\\w\\-:/.#\\[\\]]+
 */
export const RUM_EVENT_ERROR_BOUNDARY = 'com.lyt.portal.error_boundary';

/**
 * Flatten event data to primitives RUM accepts (string | number | boolean).
 *
 * @param {Object} data
 * @returns {Object}
 */
const toRumEventData = (data = {}) =>
    Object.fromEntries(
        Object.entries(data)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => {
                if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    return [key, value];
                }
                return [key, String(value)];
            }),
    );

/**
 * Record an intentional RUM custom event (ops beacon). No-op if RUM is unset.
 * Requires custom events enabled on the CloudWatch RUM app monitor.
 *
 * @param {string} type - Event type (e.g. RUM_EVENT_ERROR_BOUNDARY)
 * @param {Object} [data] - Event payload (primitives preferred)
 * @param {Object} [metadata] - Optional event metadata
 */
export const recordRumEvent = (type, data = {}, metadata) => {
    if (!awsRum || !type) {
        return;
    }

    try {
        const eventData = toRumEventData(data);
        if (metadata && typeof metadata === 'object') {
            awsRum.recordEvent(type, eventData, metadata);
        } else {
            awsRum.recordEvent(type, eventData);
        }
    } catch (error) {
        console.error('CloudWatch RUM recordEvent failed:', error);
    }
};

/**
 * Merge session attributes onto the active RUM session (e.g. agency_id after
 * login / agency switch). No-op if RUM was not initialized.
 *
 * @param {Object} attributes
 */
export const setRumSessionAttributes = (attributes = {}) => {
    if (!awsRum || !attributes || typeof attributes !== 'object') {
        return;
    }

    const cleaned = Object.fromEntries(
        Object.entries(attributes).filter(([, value]) =>
            value !== undefined && value !== null && value !== '',
        ).map(([key, value]) => [key, String(value)]),
    );

    if (Object.keys(cleaned).length === 0) {
        return;
    }

    try {
        awsRum.addSessionAttributes(cleaned);
    } catch (error) {
        console.error('CloudWatch RUM addSessionAttributes failed:', error);
    }
};

/**
 * Start a fresh anonymous RUM client on logout. aws-rum-web 3.1.0 merges
 * addSessionAttributes() for the life of the SessionManager instance;
 * startSession() rotates the session ID only and leaves prior attributes
 * (agency_id, user_role, …) in place. Reinitialize the client so only
 * bootstrap session attributes (environment, release) remain.
 */
export const resetRumSessionOnLogout = () => {
    if (!awsRum) {
        return;
    }

    const previousClient = awsRum;
    awsRum = null;

    try {
        previousClient.disable?.();
        previousClient.clearCookies?.();
    } catch (error) {
        console.error('CloudWatch RUM shutdown on logout failed:', error);
    }

    initializeCloudWatchRum();
};

/**
 * Test helper — clears the module singleton between tests.
 */
export const __resetCloudWatchRumForTests = () => {
    awsRum = null;
};
